import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@/components/Icon";
import SectionHeading from "@/components/SectionHeading";
import type { MembershipApplicationDetail } from "../queries";
import {
  APPLICATION_CRIMINAL_HISTORY_SCROLL_TARGET,
  APPLICATION_EDUCATION_SCROLL_TARGET,
  APPLICATION_EMPLOYMENT_SCROLL_TARGET,
  getApplicationTodoChecks,
  type ApplicationTodoCheck,
  type ApplicationTodoId
} from "../utils";

interface Props {
  application: MembershipApplicationDetail;
}

interface TodoItem extends ApplicationTodoCheck {
  onClick: () => void;
}

function TodoStatusIcon({ completed }: { completed: boolean }) {
  if (completed) {
    return <Icon name="check_circle" size={24} filled className="text-extended-success-brand-color shrink-0" />;
  }

  return <Icon name="check_circle" size={24} className="text-sys-brand-on-surface-variant opacity-20 shrink-0" />;
}

export default function ApplicationTodosSection({ application }: Props) {
  const navigate = useNavigate();

  const todoItems = useMemo<TodoItem[]>(() => {
    const onClickById: Record<ApplicationTodoId, () => void> = {
      "criminal-history": () =>
        navigate(
          `/people/membership-services/applications/${application.id}/details?scrollTo=${APPLICATION_CRIMINAL_HISTORY_SCROLL_TARGET}`
        ),
      education: () =>
        navigate(
          `/people/membership-services/applications/${application.id}/details?scrollTo=${APPLICATION_EDUCATION_SCROLL_TARGET}`
        ),
      employment: () =>
        navigate(
          `/people/membership-services/applications/${application.id}/details?scrollTo=${APPLICATION_EMPLOYMENT_SCROLL_TARGET}`
        ),
      "change-request": () => navigate(`/people/${application.person.pgaId}/person/change-requests`)
    };

    return getApplicationTodoChecks(application).map((check) => ({
      ...check,
      onClick: onClickById[check.id]
    }));
  }, [application, navigate]);

  if (!todoItems.length) return null;

  return (
    <>
      <SectionHeading
        heading="Application To-Dos"
        body="Manually verify the following requirements to enable approval of this application"
      />
      <div className="w-full max-w-[700px] overflow-hidden rounded-lg border border-sys-brand-outline bg-sys-brand-surface-container-low py-4">
        {todoItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className="relative flex w-full items-center gap-4 px-6 py-2 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-sys-brand-primary"
            data-testid={`application-todo-${item.id}`}
            onClick={item.onClick}
          >
            <TodoStatusIcon completed={item.completed} />
            <div className="min-w-0 flex-1">
              <div className="text-body-large-semibold text-sys-brand-on-surface">{item.title}</div>
              <div className="text-body-medium text-sys-brand-on-surface-variant">{item.description}</div>
            </div>
            <Icon name="navigate_next" size={24} className="shrink-0 text-sys-brand-on-surface-variant" />
          </button>
        ))}
      </div>
    </>
  );
}
